// Import axios for HTTP requests
import axios from 'axios';

// Import auth types
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User,
  UserWithOrganization,
  Organization,
  UpdateUserPreferencesRequest,
  ChangePasswordRequest,
  PasswordResetRequest,
  CreateUserRequest,
  UpdateUserRequest,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
} from './auth-types';

// Two separate backends:
// 1. AUTH_API - User authentication, preferences, organization management
// 2. DATA_API - Regulatory changes, citations, dashboard stats
const AUTH_API_URL = 'https://inferviaromfrontend-production.up.railway.app';
const DATA_API_URL = 'https://inferviaapi-production.up.railway.app';
// 3. ARCHIVE_API - Historical (older) regulatory documents
// Defaults to DATA_API_URL if archive endpoint is unavailable.
const ARCHIVE_API_URL =
  process.env.REACT_APP_ARCHIVE_API_URL ||
  DATA_API_URL;

// Export base URLs so other components (e.g., PDF viewer) can reuse them
export { AUTH_API_URL, DATA_API_URL, ARCHIVE_API_URL };

// Default to AUTH API for axios instance (most methods use auth)
const API_BASE_URL = AUTH_API_URL;

// Create axios instance with interceptors
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach Bearer token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors (auto-logout)
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');

      // Dispatch custom event for AuthContext to listen to
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(error);
  }
);

// Export auth types for convenience
export type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User,
  UserWithOrganization,
  Organization,
  UserRole,
  UserPreferences,
  OrganizationSettings,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateOrganizationRequest,
} from './auth-types';

// Types matching backend
export interface WhoThisAffects {
  healthcare_roles: string[];
  facility_types: string[];
  departments: string[];
}

export interface NextStep {
  action: string;
  timeline: string;
  owner: string;
  priority?: string;
}

export interface FinancialImpact {
  estimated_cost?: string;
  revenue_impact?: string;
  areas_affected: string[];
}

export interface SimplifiedAnalysis {
  // NEW: Structured summaries
  one_sentence_summary?: string;  // Short for list view
  paragraph_summary?: string;     // Full context
  detailed_changes?: string[];    // 4-7 specific bullets
  
  // OLD: Backward compatibility
  detailed_summary: string;  // Falls back to paragraph_summary
  
  who_this_affects: WhoThisAffects;
  next_steps: NextStep[];
  key_requirements?: string[];
  financial_impact?: FinancialImpact;
  compliance_deadline?: string;
  source_link: string;
}

export interface QualityControl {
  quality_score: number;
  quality_grade: string;
  safe_to_use: boolean;
  factual_accuracy?: number;
}

export interface DocumentInfo {
  pdf_url?: string;
  file_path?: string;
  file_size?: number;
  page_count?: number;
}

// Backend regulatory change response (from /api/regulatory-changes)
export interface RegulatoryChangeFromBackend {
  document_id: number;
  title: string;
  source: string;
  department: string;
  priority: string;
  agency: string;
  date: string;
  summary: string;
}

// Role-tailored content interface
export interface RoleTailoredContent {
  role: string;
  role_display_name?: string;
  summary: string;
  key_actions?: string[];
  financial_implications?: string;
  time_impact?: string;
  deadline?: string;
}

// Legacy type - keeping for compatibility
export interface RegulatoryChange {
  document_id: number;
  title: string;
  url: string;
  source: string;
  date?: string;
  scraper_type?: string;
  facility_types: string[];
  affected_departments: string[];
  simplified_analysis: SimplifiedAnalysis;
  quality_control?: QualityControl;
  discovered_date: string;
  analysis_timestamp?: string;
  document_info?: DocumentInfo;  // ← AWS S3 PDF URL and metadata
  role_tailored_content?: RoleTailoredContent[];  // ← Role-specific summaries and actions
}

export interface DashboardStats {
  total_changes: number;
  by_facility_type: Record<string, number>;
  by_department: Record<string, number>;
  by_scraper_type: Record<string, number>;
  new_this_week: number;
  average_quality_score?: number;
}

// Document extraction types
export interface DocumentSection {
  id: number;
  page: number;
  title: string;
  content: string;
}

export interface ExtractedDocument {
  id: string;
  title: string;
  date: string;
  pages: number;
  sections: DocumentSection[];
  full_text?: string;
  url?: string;
}

export interface Citation {
  id: string;
  text: string;
  docId: string;
  sectionId: number;
  highlight: string;
  page?: number;
  confidence?: number;
  source_text?: string;
  context?: string;
}

export interface DocumentWithCitations {
  documents: Record<string, ExtractedDocument>;
  citations: Citation[];
}

// API Client
export const apiClient = {
  // Health check
  async healthCheck() {
    const response = await fetch(`${API_BASE_URL}/`);
    if (!response.ok) throw new Error('Health check failed');
    return await response.json();
  },

  // Get recent regulatory changes from the DATA backend with actual data
  async getRecentChanges(limit: number = 20): Promise<RegulatoryChange[]> {
    const response = await fetch(`${DATA_API_URL}/api/changes/recent?limit=${limit}`);

    if (!response.ok) {
      throw new Error('Failed to fetch regulatory changes');
    }

    const data = await response.json();
    return data; // Returns array of RegulatoryChange directly
  },

  // ============================================================================
  // ARCHIVE ENDPOINTS
  // ============================================================================

  /**
   * Get archived regulatory changes from the archive backend
   */
  async getArchivedChanges(limit: number = 50): Promise<RegulatoryChange[]> {
    const response = await fetch(`${ARCHIVE_API_URL}/api/archive/recent?limit=${limit}`);

    if (response.status === 404) {
      console.warn('Archive endpoint not found, skipping archive data');
      return [];
    }

    if (!response.ok) {
      throw new Error('Failed to fetch archived changes');
    }

    return await response.json();
  },

  /**
   * Get archive health/summary (total doc count, status)
   */
  async getArchiveHealth(): Promise<any> {
    const response = await fetch(`${ARCHIVE_API_URL}/health`);
    if (!response.ok) {
      throw new Error('Failed to fetch archive health');
    }
    return await response.json();
  },

  /**
   * Get a single archived document
   */
  async getArchivedChange(documentId: string | number): Promise<RegulatoryChange> {
    const response = await fetch(`${ARCHIVE_API_URL}/api/archive/${documentId}`);

    if (!response.ok) {
      throw new Error('Failed to fetch archived document');
    }

    return await response.json();
  },

  // Get changes by facility type
  // TODO: Backend endpoint not implemented yet - returning empty array
  async getChangesByFacility(facilityType: string, limit: number = 10): Promise<RegulatoryChange[]> {
    console.warn('getChangesByFacility: Backend endpoint not implemented yet');
    return [];
  },

  // Get changes by department
  // TODO: Backend endpoint not implemented yet - returning empty array
  async getChangesByDepartment(department: string, limit: number = 10): Promise<RegulatoryChange[]> {
    console.warn('getChangesByDepartment: Backend endpoint not implemented yet');
    return [];
  },

  // Get changes by scraper type
  // TODO: Backend endpoint not implemented yet - returning empty array
  async getChangesByScraper(scraperType: string, limit: number = 20): Promise<RegulatoryChange[]> {
    console.warn('getChangesByScraper: Backend endpoint not implemented yet');
    return [];
  },

  // Get dashboard statistics from DATA backend
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await fetch(`${DATA_API_URL}/api/stats/dashboard`);

    if (!response.ok) {
      throw new Error('Failed to fetch dashboard stats');
    }

    return await response.json();
  },

  // NEW: Get citations for a specific document from DATA backend
  async getCitations(documentId: number): Promise<any> {
    try {
      console.log(`📋 Fetching citations from: ${DATA_API_URL}/api/changes/${documentId}/citations`);
      const response = await fetch(`${DATA_API_URL}/api/changes/${documentId}/citations`);
      
      if (!response.ok) {
        console.warn(`⚠️ Citations endpoint returned ${response.status}`);
        return null;
      }
      
      const data = await response.json();
      console.log('✅ Citations received:', data);
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch citations:', error);
      return null;
    }
  },

  // Get document with citations (combines regulatory change + citations endpoint)
  async getDocumentWithCitations(documentId: number): Promise<DocumentWithCitations> {
    try {
      console.log(`🔗 Fetching document ${documentId} data...`);

      // Get the regulatory change data
      const changes = await this.getRecentChanges(100); // Get enough to find our doc
      const change = changes.find(c => c.document_id === documentId);

      if (!change) {
        console.warn(`⚠️ Document ${documentId} not found in recent changes`);
        return this.getMockDocumentData(documentId);
      }

      console.log(`✅ Found document: ${change.title}`);

      // Get citations from YOUR endpoint
      const citationsData = await this.getCitations(documentId);

      // Build sections from the backend data using the full RegulatoryChange type
      const sections = this.buildSectionsFromAnalysis(change);

      // Convert backend citations to our format
      const citations = citationsData ? this.convertCitations(citationsData, documentId, sections) : [];

      console.log(`📊 Built ${sections.length} sections`);
      console.log(`🔍 Found ${citations.length} citations`);

      return {
        documents: {
          [`doc-${documentId}`]: {
            id: `doc-${documentId}`,
            title: change.title,
            date: change.date || new Date().toISOString().split('T')[0],
            pages: 247, // Default, update if backend provides
            sections: sections,
            url: change.url
          }
        },
        citations: citations
      };
    } catch (error) {
      console.error('❌ Failed to build document:', error);
      return this.getMockDocumentData(documentId);
    }
  },

  // Build document sections from backend change data (simplified version)
  buildSectionsFromBackendChange(change: RegulatoryChangeFromBackend): DocumentSection[] {
    const sections: DocumentSection[] = [];

    // Section 1: Summary
    sections.push({
      id: 1,
      page: 1,
      title: 'Summary',
      content: change.summary
    });

    // Section 2: Details
    sections.push({
      id: 2,
      page: 2,
      title: 'Details',
      content: `Source: ${change.source}\nDepartment: ${change.department}\nPriority: ${change.priority}\nAgency: ${change.agency}\nDate: ${change.date}`
    });

    return sections;
  },

  // Build document sections from the analysis data (legacy - for old RegulatoryChange type)
  buildSectionsFromAnalysis(change: RegulatoryChange): DocumentSection[] {
    const sections: DocumentSection[] = [];
    let sectionId = 1;
    
    // Section 1: Executive Summary
    sections.push({
      id: sectionId++,
      page: 1,
      title: 'Executive Summary',
      content: change.simplified_analysis.detailed_summary
    });
    
    // Section 2: Key Requirements
    if (change.simplified_analysis.key_requirements && change.simplified_analysis.key_requirements.length > 0) {
      sections.push({
        id: sectionId++,
        page: 2,
        title: 'Key Requirements',
        content: change.simplified_analysis.key_requirements.join('\n\n')
      });
    }
    
    // Section 3: Who This Affects
    const whoAffects = change.simplified_analysis.who_this_affects;
    sections.push({
      id: sectionId++,
      page: 3,
      title: 'Affected Parties',
      content: `Healthcare Roles: ${whoAffects.healthcare_roles.join(', ')}\n\n` +
               `Facility Types: ${whoAffects.facility_types.join(', ')}\n\n` +
               `Departments: ${whoAffects.departments.join(', ')}`
    });
    
    // Section 4: Financial Impact
    if (change.simplified_analysis.financial_impact) {
      const fi = change.simplified_analysis.financial_impact;
      sections.push({
        id: sectionId++,
        page: 4,
        title: 'Financial Impact',
        content: `Estimated Cost: ${fi.estimated_cost || 'To be determined'}\n\n` +
                 `Revenue Impact: ${fi.revenue_impact || 'Assessment pending'}\n\n` +
                 `Areas Affected: ${fi.areas_affected.join(', ')}`
      });
    }
    
    // Section 5: Next Steps
    if (change.simplified_analysis.next_steps && change.simplified_analysis.next_steps.length > 0) {
      const stepsContent = change.simplified_analysis.next_steps.map((step, idx) => 
        `${idx + 1}. ${step.action}\n   Timeline: ${step.timeline}\n   Owner: ${step.owner}${step.priority ? `\n   Priority: ${step.priority}` : ''}`
      ).join('\n\n');
      
      sections.push({
        id: sectionId++,
        page: 5,
        title: 'Implementation Steps',
        content: stepsContent
      });
    }
    
    // Section 6: Compliance Deadline
    if (change.simplified_analysis.compliance_deadline) {
      sections.push({
        id: sectionId++,
        page: 6,
        title: 'Compliance Timeline',
        content: `Compliance Deadline: ${change.simplified_analysis.compliance_deadline}\n\n` +
                 `It is critical to review and implement required changes before this deadline to ensure regulatory compliance.`
      });
    }
    
    return sections;
  },

  // Convert backend citations format to our format
  convertCitations(backendCitations: any, documentId: number, sections: DocumentSection[]): Citation[] {
    // The structure will depend on what your backend returns
    // Adapt this based on the actual response format
    
    if (Array.isArray(backendCitations)) {
      return backendCitations.map((cite, idx) => ({
        id: `claim-${idx}`,
        text: cite.text || cite.claim || cite.summary || '',
        docId: `doc-${documentId}`,
        sectionId: cite.section_id || cite.sectionId || 1,
        highlight: cite.highlight || cite.source_text || cite.text || '',
        page: cite.page || cite.page_number || 1,
        confidence: cite.confidence || 0.85
      }));
    }
    
    // If it's an object with a citations array
    if (backendCitations.citations && Array.isArray(backendCitations.citations)) {
      return backendCitations.citations.map((cite: any, idx: number) => ({
        id: `claim-${idx}`,
        text: cite.text || cite.claim || '',
        docId: `doc-${documentId}`,
        sectionId: cite.section_id || 1,
        highlight: cite.highlight || cite.source_text || '',
        page: cite.page || 1,
        confidence: cite.confidence || 0.85
      }));
    }
    
    // Generate citations from key requirements as fallback
    return [];
  },

  // Fallback mock data (only used if endpoints fail)
  getMockDocumentData(documentId: number): DocumentWithCitations {
    console.log(`📝 Using fallback mock data for document ${documentId}`);
    
    return {
      documents: {
        [`doc-${documentId}`]: {
          id: `doc-${documentId}`,
          title: 'CMS-4201-F Final Rule',
          date: 'November 10, 2024',
          pages: 247,
          sections: [
            {
              id: 1,
              page: 3,
              title: 'Executive Summary',
              content: 'This final rule updates the Medicare Advantage (MA) quality reporting requirements effective January 1, 2025. The changes include updated HEDIS measures for diabetes care management, new patient experience survey requirements (CAHPS 5.1), enhanced medication therapy management reporting, and revised star rating calculation methodology.'
            },
            {
              id: 2,
              page: 12,
              title: 'Section 3.2 - HEDIS Measure Updates',
              content: 'Updated HEDIS measures for diabetes care management will be implemented for the 2025 reporting year. These measures include HbA1c testing frequency, eye examinations, and medical attention for nephropathy. All MA plans must comply with these updated technical specifications as outlined in the HEDIS 2025 Technical Manual.'
            }
          ]
        }
      },
      citations: [
        {
          id: 'claim-1',
          text: 'New quality reporting requirements for MA plans effective January 2025',
          docId: `doc-${documentId}`,
          sectionId: 1,
          highlight: 'updated HEDIS measures for diabetes care management, new patient experience survey requirements (CAHPS 5.1)',
          page: 3,
          confidence: 0.95
        }
      ]
    };
  },

  // ============================================================================
  // AUTHENTICATION ENDPOINTS
  // ============================================================================

  /**
   * Login with email and password
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await axiosInstance.post('/api/auth/login', credentials);

    // Note: Token storage is handled by AuthContext
    return response.data;
  },

  /**
   * Register new user and organization
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Registration failed' }));
      throw new Error(error.message || 'Registration failed');
    }

    const result = await response.json();

    // Store token in localStorage
    if (result.token) {
      localStorage.setItem('auth_token', result.token);
      localStorage.setItem('user', JSON.stringify(result.user));
    }

    return result;
  },

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await axiosInstance.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Note: Token clearing is handled by AuthContext
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<UserWithOrganization> {
    const response = await axiosInstance.get('/api/auth/me');
    return response.data;
  },

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to change password' }));
      throw new Error(error.message || 'Failed to change password');
    }
  },

  /**
   * Request password reset
   */
  async requestPasswordReset(data: PasswordResetRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to request password reset');
    }
  },

  // ============================================================================
  // USER MANAGEMENT ENDPOINTS (Admin only)
  // ============================================================================

  /**
   * Get all users in organization
   */
  async getOrganizationUsers(): Promise<User[]> {
    const token = localStorage.getItem('auth_token');
    const user = this.getStoredUser();

    if (!token || !user) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/organizations/${user.organization.id}/users`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    const data = await response.json();
    return data.users || data; // Handle both paginated and non-paginated responses
  },

  /**
   * Create new user in organization
   */
  async createUser(data: CreateUserRequest): Promise<User> {
    const token = localStorage.getItem('auth_token');
    const user = this.getStoredUser();

    if (!token || !user) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/organizations/${user.organization.id}/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to create user' }));
      throw new Error(error.message || 'Failed to create user');
    }

    return await response.json();
  },

  /**
   * Update user
   */
  async updateUser(userId: string, data: UpdateUserRequest): Promise<User> {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update user');
    }

    return await response.json();
  },

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<void> {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete user');
    }
  },

  // ============================================================================
  // ORGANIZATION MANAGEMENT ENDPOINTS
  // ============================================================================

  /**
   * Get current organization
   */
  async getOrganization(): Promise<Organization> {
    const token = localStorage.getItem('auth_token');
    const user = this.getStoredUser();

    if (!token || !user) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/organizations/${user.organization.id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organization');
    }

    return await response.json();
  },

  /**
   * Update organization settings
   */
  async updateOrganization(data: UpdateOrganizationRequest): Promise<Organization> {
    const token = localStorage.getItem('auth_token');
    const user = this.getStoredUser();

    if (!token || !user) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/organizations/${user.organization.id}/settings`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update organization');
    }

    return await response.json();
  },

  // ============================================================================
  // USER PREFERENCES ENDPOINTS
  // ============================================================================

  /**
   * Get current user's preferences
   */
  async getUserPreferences(): Promise<any> {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/users/me/preferences`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch preferences');
    }

    const data = await response.json();
    return data.preferences || {};
  },

  /**
   * Update current user's preferences
   */
  async updateUserPreferences(preferences: any): Promise<any> {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/users/me/preferences`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ preferences }),
    });

    if (!response.ok) {
      throw new Error('Failed to update preferences');
    }

    const data = await response.json();
    return data.preferences || {};
  },

  /**
   * Update current user's profile
   */
  async updateUserProfile(data: { department?: string }): Promise<User> {
    const token = localStorage.getItem('auth_token');

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}/api/users/me/profile`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to update profile');
    }

    return await response.json();
  },

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  },

  /**
   * Get stored auth token
   */
  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  },

  /**
   * Get stored user
   */
  getStoredUser(): UserWithOrganization | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },
};
